import { Component, Host, Prop, h } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const COMPONENT_NAME = 'ul-breadcrumbs-type-1';

/**
 * Dark-background breadcrumb navigation component for USLegal brand.
 * Designed to be placed directly above ul-hero-header-type-1 — shares
 * the same dark navy background for visual continuity.
 *
 * @product uslegal
 * @source breadcrumb
 * @version 1.0.0
 * @category USLegal
 * @contentConfig Breadcrumb navigation on dark hero pages for USLegal
 * @example
 *   <ul-breadcrumbs-type-1 items='[{"label": "Home", "href": "/"}, {"label": "Form Library", "href": "/form-library"}, {"label": "PA BCO-10 Form"}]'></ul-breadcrumbs-type-1>
 */
@Component({
  tag: 'ul-breadcrumbs-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class ULBreadcrumbsType1 {
  /**
   * Breadcrumb navigation items displayed in order from root to current page.
   * @content
   * @type {array}
   * @items.type {object}
   * @items.property {string} label - Visible breadcrumb text (e.g. "Home", "Form Library")
   * @items.property {string} href - Navigation URL. Omit for the last (current page) item to render it as plain text.
   * @items.required label
   * @items.content label
   * @minItems 1
   */
  @Prop({ mutable: true }) items?: any[] | string;

  /**
   * Add schema.org BreadcrumbList microdata markup.
   * @default true
   */
  @Prop() withSchemaOrgEntities: boolean = true;

  @Prop() colorMode: 'uslegal' | 'inverted' = 'uslegal';

  private parsedItems: any[] = [];

  componentWillLoad() {
    const parsed = parseJsonProp(this.items, []);
    this.parsedItems = Array.isArray(parsed) ? parsed : [];
  }

  render() {
    const items = this.parsedItems;
    if (!items || items.length === 0) return null;

    return (
      <Host data-color-mode={this.colorMode}>
        {/* Own section + container instead of me-section: me-section always renders a
            nested me-container, and this block is above the fold, so that dependency
            put the whole layout class matrix into critical CSS. The outer element keeps
            spanning the viewport — it carries the dark background. */}
        <section class={`${COMPONENT_NAME}__section`}>
          <div class={`${COMPONENT_NAME}__container`}>
            <nav
              class={COMPONENT_NAME}
              aria-label="Breadcrumb"
              role="navigation"
              {...(this.withSchemaOrgEntities ? { itemscope: true, itemtype: 'https://schema.org/BreadcrumbList' } : {})}
            >
              <ol class={`${COMPONENT_NAME}__list`}>
                {items.map((item, index) => {
                  const isLast = index === items.length - 1;
                  return (
                    <li
                      class={`${COMPONENT_NAME}__item${isLast ? ` ${COMPONENT_NAME}__item--current` : ''}`}
                      {...(this.withSchemaOrgEntities ? { itemscope: true, itemtype: 'https://schema.org/ListItem', itemprop: 'itemListElement' } : {})}
                    >
                      {item.href && !isLast ? (
                        <a class={`${COMPONENT_NAME}__link`} href={item.href} aria-label={item.label} {...(this.withSchemaOrgEntities ? { itemprop: 'item' } : {})}>
                          <span {...(this.withSchemaOrgEntities ? { itemprop: 'name' } : {})}>{item.label}</span>
                        </a>
                      ) : (
                        <span class={`${COMPONENT_NAME}__text`} aria-current={isLast ? 'page' : undefined} {...(this.withSchemaOrgEntities ? { itemprop: 'name' } : {})}>
                          {item.label}
                        </span>
                      )}
                      {this.withSchemaOrgEntities && isLast && item.href && <meta itemprop="item" content={item.href} />}
                      {!isLast && (
                        <span class={`${COMPONENT_NAME}__separator`} aria-hidden="true">
                          <me-icon name="arrows--chevron-right" inline />
                        </span>
                      )}
                      {this.withSchemaOrgEntities && <meta itemprop="position" content={String(index + 1)} />}
                    </li>
                  );
                })}
              </ol>
            </nav>
          </div>
        </section>
      </Host>
    );
  }
}
